/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, {
  useMemo, useRef, useEffect, useState, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import MonthDayDroppableCell from './MonthDayDroppableCell';
import MonthEditPopover from './MonthEditPopover';
import MonthEditPopoverContext from './MonthEditPopoverContext';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useCalendarKeyboardScroll from '../../hooks/useCalendarKeyboardScroll';
import {
  removePresaveItemFromRedux,
  setSelectedItems,
  setMonthSelectionActiveDate,
} from '../../ducks/calendarActions';
import { flushDebounceForTable } from './hooks/debounceHandler';
import { store } from '../../../../redux_setup/store';

const MonthContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #EEEEEE;
  height: 100%;
  width: 100%;
  /** Prevent the browser's native Shift+Click range text-selection from
   * highlighting the whole page when building a multi-selection. The edit
   * popover is portaled outside this container, so its inputs stay
   * selectable/editable. */
  user-select: none;
  /* Both axes scroll on this single container so it's the unique
   * "closest scrollable ancestor" rbd detects for our Droppables.
   * rbd watches its scroll events and recomputes droppable positions —
   * if vertical scroll happened on an ancestor outside rbd's awareness
   * (e.g. the page-level LayoutContainer), drops on newly-revealed
   * cells would land on stale positions. */
  overflow: auto;
`;

const ScrollIconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid #EEEEEE;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  
   &:hover {
    border-color: #bdbdbd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: #F5F5F5;
    transform: scale(0.96);
  }

`;

const WeekRow = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex: 1;
`;

const MonthView = ({ isMonthItemsLoading }) => {
  const monthContainerRef = useRef(null);
  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const leftRafId = useRef(null);
  const rightRafId = useRef(null);

  useCalendarKeyboardScroll({
    getScrollNode: () => monthContainerRef.current,
    isActive: true,
  });

  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const dragIsInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );

  const dispatch = useDispatch();

  /** Single popover instance for the whole month grid. Clicking another
   * exercise replaces `editingItem` so the popover re-anchors instead
   * of stacking. We store only the identifying bits (assignedId, date,
   * anchor); the live exercise content is looked up from Redux below
   * so swaps/edits to the item update the popover too. */
  const [editingItem, setEditingItem] = useState(null);

  /** Refs that let closePopover read the latest values without
   * recreating on every liveContent change. Updated each render. */
  const liveContentRef = useRef(null);
  const editingItemRef = useRef(null);

  const closePopover = useCallback(() => {
    const item = liveContentRef.current;
    const editing = editingItemRef.current;

    /** Flush any in-flight debounced table save so changes the coach
     * made (e.g. editing reps mid-keystroke) land immediately rather
     * than firing later without context. */
    if (item?.assignedId) {
      flushDebounceForTable(item.assignedId);
    }

    /** If the popover is closing on an unsaved preSave item (the user
     * added via "+" but didn't save), clean it up. Without this the
     * item stays as an orphaned preSave row with no visual indicator. */
    if (item?.preSave && editing) {
      dispatch(removePresaveItemFromRedux(
        moment(editing.date).format('YYYY-MM-DD'),
        null,
        item.assignedId,
      ));
    }
    setEditingItem(null);
  }, [dispatch]);

  /** Mousedown anywhere in the grid that isn't an exercise row or the
   * selection action bar. Item rows own their own active-day/selection
   * handling (and we must NOT touch monthSelectionActiveDate before the
   * item's different-day check reads it), so bail for those. Otherwise:
   *  - empty space inside a day cell → make that day active, clear selection
   *  - the grid background / gaps → clear selection and active day
   * Shift is a no-op here (selection building happens on item rows). */
  const handleGridMouseDown = useCallback((e) => {
    if (e.shiftKey) return;
    if (e.target.closest('[data-assigned-id]')) return;
    if (e.target.closest('[data-month-action-bar]')) return;
    /** reactjs-popup (e.g. the bulk Superset picker) portals its content into
     * #popup-root on document.body. React still bubbles the synthetic event
     * through the React tree to this handler, but e.target lives in the
     * portal — so the DOM-based [data-month-action-bar] guard above misses
     * it. Bail for any click originating inside a portaled popup so picking a
     * superset letter (or any popup action) never clears the selection. */
    if (e.target.closest('#popup-root')) return;

    const { selectedItems } = store.getState().calendar.data;
    const hasSelection = selectedItems && Object.keys(selectedItems).length > 0;
    const dayCell = e.target.closest('[data-month-day-cell]');

    if (dayCell) {
      if (hasSelection) dispatch(setSelectedItems({}));
      dispatch(setMonthSelectionActiveDate(dayCell.getAttribute('data-date')));
    } else {
      if (hasSelection) dispatch(setSelectedItems({}));
      dispatch(setMonthSelectionActiveDate(null));
    }
  }, [dispatch]);

  const popoverContextValue = useMemo(() => ({
    openPopover: ({ content, date, anchorEl }) => {
      if (!anchorEl || !content) return;
      /** Clicking the row whose popover is already open toggles it
       * closed — matches users' expectation that the row click is a
       * toggle, not a no-op. */
      if (editingItem?.assignedId === content.assignedId) {
        setEditingItem(null);
        return;
      }
      setEditingItem({ assignedId: content.assignedId, date, anchorEl });
    },
    activeAssignedId: editingItem?.assignedId ?? null,
    /** Lets the shared ReadOnlyExerciseItem month-gate its Shift+Click
     * multi-select behavior without affecting day/week (the default
     * context value has isMonthView false). */
    isMonthView: true,
  }), [editingItem]);

  const editingDateKey = editingItem
    ? moment(editingItem.date).format('YYYY-MM-DD')
    : null;
  const liveExercises = editingDateKey
    ? calendarItems[editingDateKey]?.exercises
    : null;
  const liveContentIndex = liveExercises
    ? liveExercises.findIndex((ex) => ex.assignedId === editingItem.assignedId)
    : -1;
  const liveContent = liveContentIndex >= 0 ? liveExercises[liveContentIndex] : null;

  /** Keep refs in sync each render so closePopover can read the latest
   * values without a stale closure. */
  liveContentRef.current = liveContent;
  editingItemRef.current = editingItem;

  /** If the item we were editing disappears (e.g. deleted), close the
   * popover so we don't render stale content. */
  useEffect(() => {
    if (editingItem && !liveContent) {
      setEditingItem(null);
    }
  }, [editingItem, liveContent]);

  /** Auto-open the preSave popover when "Create New Exercise" dispatches
   * a new item via the modal (the picker's own open-flag was cleared by
   * the modal overlay before the item landed). Scoped to preSave items
   * only — does not attempt to keep the popover open on swaps. */
  useEffect(() => {
    if (!mostRecentlyAddedCalendarItem?.preSave) return;
    if (editingItem?.assignedId === mostRecentlyAddedCalendarItem.assignedId) return;
    const dateKey = mostRecentlyAddedCalendarItem.date;
    if (!dateKey) return;
    requestAnimationFrame(() => {
      const row = document.querySelector(
        `[data-assigned-id="${mostRecentlyAddedCalendarItem.assignedId}"]`,
      );
      if (!row) return;
      setEditingItem({
        assignedId: mostRecentlyAddedCalendarItem.assignedId,
        date: moment(dateKey),
        anchorEl: row,
      });
    });
  }, [mostRecentlyAddedCalendarItem]);

  const scrollLeftStep = () => {
    const el = monthContainerRef.current;
    if (!el) return;

    if (el.scrollLeft > 0) {
      el.scrollBy({ left: -5 });
      leftRafId.current = requestAnimationFrame(scrollLeftStep);
    } else {
      leftRafId.current = null;
    }
  };

  const scrollRightStep = () => {
    const el = monthContainerRef.current;
    if (el.scrollLeft + el.clientWidth < el.scrollWidth) {
      el.scrollBy({ left: 5 });
      rightRafId.current = requestAnimationFrame(scrollRightStep);
    } else {
      rightRafId.current = null;
    }
  };

  const handleLeftMouseDown = () => {
    if (!leftRafId.current) {
      leftRafId.current = requestAnimationFrame(scrollLeftStep);
    }
  };

  const handleLeftMouseUp = () => {
    if (leftRafId.current) {
      cancelAnimationFrame(leftRafId.current);
      leftRafId.current = null;
    }
  };

  const handleRightMouseDown = () => {
    if (!rightRafId.current) {
      rightRafId.current = requestAnimationFrame(scrollRightStep);
    }
  };

  const handleRightMouseUp = () => {
    if (rightRafId.current) {
      cancelAnimationFrame(rightRafId.current);
      rightRafId.current = null;
    }
  };

  /** Drag-time autoscroll.
   *
   * rbd's built-in autoscroll has trouble with nested scroll containers,
   * and hooking edge-arrow buttons via hover doesn't work either —
   * during a drag the dragged element follows the cursor and sits on
   * top of the arrows, so mouseenter never fires on them.
   *
   * Instead, while a drag is active we listen for mousemove on the
   * document, measure the cursor's distance from each MonthContainer
   * edge, and drive our own RAF scroll loop. Speed ramps up as the
   * cursor approaches the edge. Both axes scroll on MonthContainer
   * itself — the same element rbd treats as the closest scrollable
   * ancestor of our Droppables — so rbd recomputes droppable positions
   * as content scrolls and drops land on the right cell. */
  useEffect(() => {
    if (!dragIsInProgress) return undefined;

    const EDGE_THRESHOLD = 80; /** px from edge where autoscroll kicks in */
    const MAX_SPEED = 18; /** px per frame at the very edge */
    let rafId = null;
    let speedX = 0;
    let speedY = 0;

    const tick = () => {
      const monthEl = monthContainerRef.current;
      if (!monthEl) {
        rafId = null;
        return;
      }
      if (speedX !== 0) monthEl.scrollBy({ left: speedX });
      if (speedY !== 0) monthEl.scrollBy({ top: speedY });
      if (speedX !== 0 || speedY !== 0) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };

    const computeSpeed = (distance) => {
      const ratio = 1 - distance / EDGE_THRESHOLD;
      return Math.ceil(MAX_SPEED * ratio);
    };

    const onMouseMove = (event) => {
      const monthEl = monthContainerRef.current;
      if (!monthEl) return;

      /** Use MonthContainer's own bounds so the trigger zones live at
       * the calendar's edges, not the viewport's. */
      const rect = monthEl.getBoundingClientRect();
      const { clientX, clientY } = event;
      const distLeft = clientX - rect.left;
      const distRight = rect.right - clientX;
      const distTop = clientY - rect.top;
      const distBottom = rect.bottom - clientY;

      const insideX = clientX >= rect.left && clientX <= rect.right;
      const insideY = clientY >= rect.top && clientY <= rect.bottom;

      if (insideY && distLeft >= 0 && distLeft < EDGE_THRESHOLD) {
        speedX = -computeSpeed(distLeft);
      } else if (insideY && distRight >= 0 && distRight < EDGE_THRESHOLD) {
        speedX = computeSpeed(distRight);
      } else {
        speedX = 0;
      }

      if (insideX && distTop >= 0 && distTop < EDGE_THRESHOLD) {
        speedY = -computeSpeed(distTop);
      } else if (insideX && distBottom >= 0 && distBottom < EDGE_THRESHOLD) {
        speedY = computeSpeed(distBottom);
      } else {
        speedY = 0;
      }

      if ((speedX !== 0 || speedY !== 0) && !rafId) {
        rafId = requestAnimationFrame(tick);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [dragIsInProgress]);

  useEffect(() => {
    const checkOverflow = () => {
      const el = monthContainerRef.current;
      if (!el) return;

      if (leftScrollRef.current) {
        leftScrollRef.current.style.visibility = el.scrollLeft > 0 ? 'visible' : 'hidden';
      }

      if (rightScrollRef.current) {
        rightScrollRef.current.style.visibility = el.scrollLeft + el.clientWidth < el.scrollWidth
          ? 'visible'
          : 'hidden';
      }
    };

    checkOverflow();

    const el = monthContainerRef.current;
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);

    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const weeks = useMemo(() => {
    const monthStart = moment(selectedDate).startOf('month');
    const gridStart = monthStart.clone().startOf('week');
    const gridEnd = moment(selectedDate).endOf('month').endOf('week');
    const totalDays = gridEnd.diff(gridStart, 'days') + 1;

    const allDates = [];
    for (let i = 0; i < totalDays; i += 1) {
      allDates.push(gridStart.clone().add(i, 'days'));
    }

    const numWeeks = totalDays / 7;
    const result = [];
    for (let i = 0; i < numWeeks; i += 1) {
      result.push(allDates.slice(i * 7, (i + 1) * 7));
    }

    return result;
  }, [selectedDate]);

  const isCurrentMonthDay = (date) => date.month() === moment(selectedDate).month();

  return (
    <MonthEditPopoverContext.Provider value={popoverContextValue}>
      <ScrollIconBox
        ref={leftScrollRef}
        style={{
          position: 'fixed',
          top: '50%',
          left: (isPrimaryAnimatedSideBarOpen
            ? (window.innerWidth < 1023 ? 360 : 444)
            : 60),
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleLeftMouseDown}
        onMouseUp={handleLeftMouseUp}
        onMouseLeave={handleLeftMouseUp}
      >
        <IcomoonReact iconSet={iconSet} size={14} icon='left-arrow' color='#9E9E9E' />
      </ScrollIconBox>

      <MonthContainer ref={monthContainerRef} onMouseDown={handleGridMouseDown}>
        {weeks.map((week) => (
          <WeekRow key={week[0].format('YYYY-MM-DD')}>
            {week.map((date) => {
              const dateKey = date.format('YYYY-MM-DD');
              const dayData = calendarItems[dateKey];
              const isPaddingDay = !isCurrentMonthDay(date);

              return (
                <MonthDayDroppableCell
                  key={dateKey}
                  date={date}
                  dayData={dayData}
                  isPaddingDay={isPaddingDay}
                  isMonthItemsLoading={isMonthItemsLoading}
                  programType={programType}
                  isAnalyticsDisabled={isAnalyticsDisabled}
                />
              );
            })}
          </WeekRow>
        ))}
      </MonthContainer>

      <ScrollIconBox
        ref={rightScrollRef}
        style={{
          position: 'fixed',
          top: '50%',
          right: 12,
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleRightMouseDown}
        onMouseUp={handleRightMouseUp}
        onMouseLeave={handleRightMouseUp}
      >
        <IcomoonReact iconSet={iconSet} size={14} icon='right-arrow' color='#9E9E9E' />
      </ScrollIconBox>
      {editingItem && liveContent && (
        <MonthEditPopover
          key={editingItem.assignedId}
          content={liveContent}
          index={liveContentIndex}
          date={editingItem.date}
          anchorEl={editingItem.anchorEl}
          onClose={closePopover}
          scrollContainerRef={monthContainerRef}
        />
      )}
    </MonthEditPopoverContext.Provider>
  );
};

export default MonthView;
